import { ApiResponse } from '@shared/types';
import {
  AccessControlRecord,
  AccessControlArea,
  AccessPolicyControlRecord,
  AccessPolicyRecord,
  AccessPolicySummary,
  RolePolicyAssignmentRecord,
  SaveAccessPolicyRequest,
  SaveRolePolicyAssignmentRequest,
  SecuritySnapshot,
} from '../types/accessControl';
import { ACCESS_CONTROL_CACHE_KEY } from '../lib/auth/sessionStorage';
import { supabase } from './supabase';

// ── Offline access-control-code cache ─────────────────────────────────────────
// Every permission check across the app (order creation, KOT confirmation,
// settlements, etc.) reads from state.auth.accessControlCodes, which used to
// be populated purely from a live RPC call with no offline fallback — unlike
// the user profile, which already has a full per-user offline cache in
// authService.ts. That meant a user who signed in offline via the cached
// profile ended up authenticated but effectively permission-less, since the
// RPC call always fails offline and the thunk fell back to an empty array.
// Keyed by user id, mirroring authService.ts's cache shape, so multiple staff
// on a shared terminal each keep their own last-known permission set.

function getAccessControlCacheMap(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(ACCESS_CONTROL_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
  } catch {
    return {};
  }
}

function cacheAccessControlCodes(userId: string, codes: string[]): void {
  try {
    const map = getAccessControlCacheMap();
    map[userId] = codes;
    localStorage.setItem(ACCESS_CONTROL_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* ignore — localStorage unavailable */
  }
}

function getCachedAccessControlCodes(userId: string): string[] | null {
  try {
    const map = getAccessControlCacheMap();
    return map[userId] ?? null;
  } catch {
    return null;
  }
}

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapAccessControl = (
  value: Record<string, unknown>,
): AccessControlRecord => ({
  id: String(value.id),
  code: String(value.code),
  name: String(value.name || ''),
  description: String(value.description || ''),
  area: String(value.area || AccessControlArea.DASHBOARD) as AccessControlArea,
  sort_order: toNumber(value.sort_order),
  created_at: value.created_at ? String(value.created_at) : undefined,
  updated_at: value.updated_at ? String(value.updated_at) : undefined,
});

const mapAccessPolicy = (
  value: Record<string, unknown>,
): AccessPolicyRecord => ({
  id: String(value.id),
  company_id: toNumber(value.company_id),
  name: String(value.name || ''),
  description: (value.description as string | null) || null,
  created_by: String(value.created_by || ''),
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

const mapPolicyControl = (
  value: Record<string, unknown>,
): AccessPolicyControlRecord => ({
  id: String(value.id),
  policy_id: String(value.policy_id || ''),
  access_control_id: String(value.access_control_id || ''),
  added_by: String(value.added_by || ''),
  created_at: String(value.created_at || ''),
});

const mapRolePolicyAssignment = (
  value: Record<string, unknown>,
): RolePolicyAssignmentRecord => ({
  id: String(value.id),
  company_id: toNumber(value.company_id),
  outlet_id: String(value.outlet_id || ''),
  role: String(value.role || '') as RolePolicyAssignmentRecord['role'],
  policy_id: String(value.policy_id || ''),
  assigned_by: String(value.assigned_by || ''),
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

export const fetchAccessControls = async (): Promise<
  ApiResponse<AccessControlRecord[]>
> => {
  try {
    const { data, error } = await supabase
      .from('access_controls')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) => mapAccessControl(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch access controls',
      data: [],
    };
  }
};

/**
 * Fetches the current user's access control codes. Pass the current user's
 * id whenever it's known so the result can be cached, and so a failed
 * (offline) fetch can fall back to whatever was last cached for that user
 * instead of silently returning an empty list — which would otherwise leave
 * an offline-signed-in user authenticated but permission-less.
 */
export const fetchCurrentUserAccessControlCodes = async (
  userId?: string,
): Promise<ApiResponse<string[]>> => {
  try {
    const { data, error } = await supabase.rpc(
      'get_auth_user_access_control_codes',
    );

    if (error) {
      if (userId) {
        const cached = getCachedAccessControlCodes(userId);
        if (cached) return { success: true, data: cached };
      }
      return { success: false, error: error.message, data: [] };
    }

    const codes = Array.isArray(data)
      ? data.map((value) => String(value))
      : ([] as string[]);

    if (userId) cacheAccessControlCodes(userId, codes);

    return {
      success: true,
      data: codes,
    };
  } catch (error) {
    if (userId) {
      const cached = getCachedAccessControlCodes(userId);
      if (cached) return { success: true, data: cached };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch current user access controls',
      data: [],
    };
  }
};

export const fetchAccessPolicies = async (
  companyId: number,
): Promise<ApiResponse<AccessPolicyRecord[]>> => {
  try {
    const { data, error } = await supabase
      .from('access_policies')
      .select('*')
      .eq('company_id', companyId)
      .order('name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) => mapAccessPolicy(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch access policies',
      data: [],
    };
  }
};

export const fetchAccessPolicyControls = async (
  policyIds: string[],
): Promise<ApiResponse<AccessPolicyControlRecord[]>> => {
  if (!policyIds.length) {
    return { success: true, data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('access_policy_controls')
      .select('*')
      .in('policy_id', policyIds);

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) => mapPolicyControl(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch policy access controls',
      data: [],
    };
  }
};

export const fetchRolePolicyAssignments = async (
  companyId: number,
  outletId?: string | null,
): Promise<ApiResponse<RolePolicyAssignmentRecord[]>> => {
  try {
    let query = supabase
      .from('role_policy_assignments')
      .select('*')
      .eq('company_id', companyId)
      .order('outlet_id', { ascending: true })
      .order('role', { ascending: true });

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) => mapRolePolicyAssignment(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch role policy assignments',
      data: [],
    };
  }
};

export const fetchSecuritySnapshot = async (
  companyId: number,
  outletId?: string | null,
): Promise<ApiResponse<SecuritySnapshot>> => {
  try {
    const [controlsResponse, policiesResponse, assignmentsResponse] =
      await Promise.all([
        fetchAccessControls(),
        fetchAccessPolicies(companyId),
        fetchRolePolicyAssignments(companyId, outletId),
      ]);

    if (!controlsResponse.success) {
      return {
        success: false,
        error: controlsResponse.error,
        data: {
          controls: [],
          policies: [],
          assignments: [],
        },
      };
    }

    if (!policiesResponse.success) {
      return {
        success: false,
        error: policiesResponse.error,
        data: {
          controls: controlsResponse.data || [],
          policies: [],
          assignments: assignmentsResponse.data || [],
        },
      };
    }

    if (!assignmentsResponse.success) {
      return {
        success: false,
        error: assignmentsResponse.error,
        data: {
          controls: controlsResponse.data || [],
          policies: [],
          assignments: [],
        },
      };
    }

    const policies = policiesResponse.data || [];
    const controls = controlsResponse.data || [];
    const assignments = assignmentsResponse.data || [];
    const policyControlResponse = await fetchAccessPolicyControls(
      policies.map((policy) => policy.id),
    );

    if (!policyControlResponse.success) {
      return {
        success: false,
        error: policyControlResponse.error,
        data: {
          controls,
          policies: [],
          assignments,
        },
      };
    }

    const controlMap = new Map(
      controls.map((control) => [control.id, control]),
    );
    const controlsByPolicy = new Map<string, string[]>();

    (policyControlResponse.data || []).forEach((mapping) => {
      const existing = controlsByPolicy.get(mapping.policy_id) || [];
      existing.push(mapping.access_control_id);
      controlsByPolicy.set(mapping.policy_id, existing);
    });

    const assignmentCountByPolicy = assignments.reduce<Record<string, number>>(
      (accumulator, assignment) => {
        accumulator[assignment.policy_id] =
          (accumulator[assignment.policy_id] || 0) + 1;
        return accumulator;
      },
      {},
    );

    const hydratedPolicies: AccessPolicySummary[] = policies.map((policy) => {
      const controlIds = controlsByPolicy.get(policy.id) || [];
      return {
        ...policy,
        control_ids: controlIds,
        controls: controlIds
          .map((controlId) => controlMap.get(controlId))
          .filter(Boolean) as AccessControlRecord[],
        assignment_count: assignmentCountByPolicy[policy.id] || 0,
      };
    });

    return {
      success: true,
      data: {
        controls,
        policies: hydratedPolicies,
        assignments,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch security snapshot',
      data: {
        controls: [],
        policies: [],
        assignments: [],
      },
    };
  }
};

export const saveAccessPolicy = async (
  request: SaveAccessPolicyRequest,
): Promise<ApiResponse<AccessPolicyRecord>> => {
  try {
    const payload = {
      company_id: request.companyId,
      name: request.name.trim(),
      description: request.description?.trim() || null,
    };

    let policy: AccessPolicyRecord | null = null;

    if (request.id) {
      const { data, error } = await supabase
        .from('access_policies')
        .update(payload)
        .eq('id', request.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      policy = mapAccessPolicy(data);
    } else {
      const { data, error } = await supabase
        .from('access_policies')
        .insert({
          ...payload,
          created_by: request.createdBy,
        })
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      policy = mapAccessPolicy(data);
    }

    const policyId = policy.id;
    const { data: existingMappings, error: existingError } = await supabase
      .from('access_policy_controls')
      .select('access_control_id')
      .eq('policy_id', policyId);

    if (existingError) {
      return { success: false, error: existingError.message, data: undefined };
    }

    const existingIds = new Set(
      (existingMappings || []).map((mapping) =>
        String(mapping.access_control_id),
      ),
    );
    const nextIds = new Set(request.controlIds);

    const idsToDelete = Array.from(existingIds).filter(
      (id) => !nextIds.has(id),
    );
    const idsToInsert = Array.from(nextIds).filter(
      (id) => !existingIds.has(id),
    );

    if (idsToDelete.length) {
      const { error } = await supabase
        .from('access_policy_controls')
        .delete()
        .eq('policy_id', policyId)
        .in('access_control_id', idsToDelete);

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }
    }

    if (idsToInsert.length) {
      const { error } = await supabase.from('access_policy_controls').insert(
        idsToInsert.map((accessControlId) => ({
          policy_id: policyId,
          access_control_id: accessControlId,
          added_by: request.createdBy,
        })),
      );

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }
    }

    return { success: true, data: policy };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save policy',
      data: undefined,
    };
  }
};

export const deleteAccessPolicy = async (
  policyId: string,
): Promise<ApiResponse> => {
  try {
    const { error } = await supabase
      .from('access_policies')
      .delete()
      .eq('id', policyId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete policy',
    };
  }
};

export const saveRolePolicyAssignment = async (
  request: SaveRolePolicyAssignmentRequest,
): Promise<ApiResponse> => {
  try {
    if (!request.policyId) {
      const { error } = await supabase
        .from('role_policy_assignments')
        .delete()
        .eq('company_id', request.companyId)
        .eq('outlet_id', request.outletId)
        .eq('role', request.role);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    }

    const { error } = await supabase.from('role_policy_assignments').upsert(
      {
        company_id: request.companyId,
        outlet_id: request.outletId,
        role: request.role,
        policy_id: request.policyId,
        assigned_by: request.assignedBy,
      },
      {
        onConflict: 'outlet_id,role',
      },
    );

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save role policy assignment',
    };
  }
};
